import type {
  AssignmentEvent,
  Conversation,
  Counters,
  InboxChanges,
  ThreadItem,
} from '../types/whatsapp';
import { mergeConversations } from './conversationList';
import type { ListView } from './conversationList';
import { mergeThread } from './thread';

// Pure rules of the inbox polling: backoff, what to apply from a `changes` response
// and the Spanish wording of assignment reasons. The provider only wires these to React.

export const POLL_MS = 5_000;
const MAX_BACKOFF_MS = 30_000;

/** Delay before the next poll: 5 s, doubling after each consecutive failure up to 30 s. */
export const nextDelay = (failures: number): number =>
  failures <= 0 ? POLL_MS : Math.min(POLL_MS * 2 ** failures, MAX_BACKOFF_MS);

export const viewKey = (v: ListView): string => `${v.filter}|${v.unanswered ? 1 : 0}|${v.q.trim()}`;

export interface InboxData {
  list: Conversation[];
  hasMore: boolean;
  counters: Counters | null;
  thread: ThreadItem[];
}

/** What the page showed when the request left; compared with what it shows when the answer arrives. */
export interface PollContext {
  viewKey: string;
  openId: number | null;
}

export interface ApplyResult {
  data: InboxData;
  /** The list must be requested again (search active). */
  refetchList: boolean;
  /** The open conversation changed: refresh its header and side panel. */
  refreshDetail: boolean;
}

/**
 * Applies a `changes` response. The counters are always taken. The conversations are
 * dropped when the view changed in the meantime (a list fetch is already running) and
 * the thread items when another conversation was opened; the 30 s overlap of the
 * server's cursor delivers again whatever falls in that gap.
 */
export const applyChanges = (
  data: InboxData,
  resp: InboxChanges,
  sent: PollContext,
  current: PollContext,
  view: ListView,
): ApplyResult => {
  let { list } = data;
  let refetchList = false;
  if (sent.viewKey === current.viewKey && resp.conversations.length > 0) {
    const merged = mergeConversations(list, resp.conversations, view, data.hasMore);
    list = merged.items;
    refetchList = merged.needsRefetch;
  }

  let { thread } = data;
  let refreshDetail = false;
  const sameOpen = sent.openId !== null && sent.openId === current.openId;
  if (sameOpen) {
    if (resp.messages.length > 0 || resp.events.length > 0) {
      thread = mergeThread(thread, resp.messages, resp.events);
    }
    refreshDetail = resp.events.length > 0 || resp.conversations.some((c) => c.id === sent.openId);
  }

  return { data: { ...data, list, thread, counters: resp.counters }, refetchList, refreshDetail };
};

const REASON_TEXT: Record<string, string> = {
  handoff_urgent: 'posible urgencia',
  handoff_clinical: 'consulta clínica',
  handoff_requested: 'el paciente pidió hablar con una persona',
  handoff_topic_off: 'tema apagado',
  handoff_low_confidence: 'no se entendió la consulta',
  handoff_attachment: 'el paciente envió archivos',
  handoff_unverified: 'respuesta sin verificar',
  handoff_frustration: 'paciente molesto',
  handoff_loop: 'pregunta repetida',
  handoff_assistant_off: 'asistente apagado',
  handoff_service_error: 'error del servicio',
  manual_take: 'tomada por el equipo',
  staff_reply: 'el equipo respondió',
  manual_return: 'devuelta al asistente',
  window_expired: 'venció la ventana de 24 h',
};

const capitalize = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** Short reason for the context panel ("Posible urgencia", "Tema apagado"). */
export const reasonLabel = (reason: string): string => capitalize(REASON_TEXT[reason] ?? 'traspaso al equipo');

/** The line shown in the thread for an assignment change. */
export const eventLabel = (e: AssignmentEvent): string => {
  if (e.to === 'human-agent') {
    if (e.actor) return `Tomada por ${e.actor.name}`;
    const base = REASON_TEXT[e.reason] ?? 'traspaso';
    const detail = e.reason === 'handoff_topic_off' && e.topic_label ? ` (${e.topic_label})` : '';
    return `Traspaso al equipo: ${base}${detail}`;
  }
  if (e.actor) return `Devuelta al asistente por ${e.actor.name}`;
  if (e.reason === 'window_expired') return 'Devuelta al asistente: venció la ventana de 24 h';
  return 'Devuelta al asistente';
};

/** Body of the browser notification for an automatic handoff. */
export const handoffNotificationBody = (reason: string, urgent: boolean): string =>
  urgent ? 'Posible urgencia: la conversación pasó al equipo' : `Pasó al equipo: ${REASON_TEXT[reason] ?? 'traspaso'}`;
